import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, NotificationModal } from '../../../shared/components/ui';
import MealCard from '../../meal/components/MealCard';
import MealFeedbackActions from '../../meal/components/MealFeedbackActions';
import RecipeSelectorModal from '../../meal/components/RecipeSelectorModal';
import { mealService } from '../../meal/services/mealService';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

const MenuDisplay = ({ menu, onConfirm, canGiveFeedback = true, feedbackService, canReorder = false, onMealReorder, onToggleRepeatable, menuMetadata = null }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [feedback, setFeedback] = useState({});
    const [evaluatedMeals, setEvaluatedMeals] = useState({});
    const [localMenu, setLocalMenu] = useState(menu);
    const [isReordering, setIsReordering] = useState(false);
    const [repeatableStates, setRepeatableStates] = useState({});

    // Estados para RecipeSelectorModal
    const [isRecipeSelectorOpen, setIsRecipeSelectorOpen] = useState(false);
    const [selectedMealToReplace, setSelectedMealToReplace] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);

    // Estados para NotificationModal
    const [notification, setNotification] = useState({ isOpen: false, type: 'success', message: '' });

    // Configurar sensores para drag & drop (incluyendo touch para mobile)
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Resetear estados cuando cambie el menú
    useEffect(() => {
        setFeedback({});
        setEvaluatedMeals({});
        setLocalMenu(menu);

        // Inicializar estados de repeatable desde los datos del menú
        const newRepeatableStates = {};
        if (menu) {
            Object.keys(menu).forEach((mealKey) => {
                const meal = menu[mealKey];
                if (meal && meal.id) {
                    newRepeatableStates[meal.id] = meal.isRepeatable || false;
                }
            });
        }
        setRepeatableStates(newRepeatableStates);
    }, [menu]);

    // Función para manejar el feedback de cada plato
    const handleFeedback = async (comidaKey, platoNombre, isLiked) => {

        const newFeedback = {
            ...feedback,
            [comidaKey]: {
                dish: platoNombre,
                liked: isLiked
            }
        };

        setFeedback(newFeedback);

        // Marcar la comida como evaluada
        setEvaluatedMeals(prev => ({
            ...prev,
            [comidaKey]: {
                evaluated: true,
                liked: isLiked
            }
        }));

        // Enviar feedback inmediatamente
        try {
            const feedbackData = [{
                meal: comidaKey,
                dish: platoNombre,
                liked: isLiked
            }];

            await feedbackService.sendFeedback(feedbackData);
            
        } catch (error) {
        }
    };

    // Función para enviar feedback al backend
    const sendFeedback = async () => {
        try {
            const feedbackData = Object.entries(feedback).map(([comidaKey, data]) => ({
                meal: comidaKey,
                dish: data.dish,
                liked: data.liked
            }));

            if (feedbackData.length > 0) {
                await feedbackService.sendFeedback(feedbackData);
            }
        } catch (error) {
        }
    };

    // Función para confirmar menú con feedback
    const handleConfirm = async () => {
        await sendFeedback();
        onConfirm();
    };

    // Función para manejar el reordenamiento de meals
    const handleDragEnd = async (event) => {
        if (!canReorder || !onMealReorder) return;

        const { active, over } = event;

        if (!over || active.id === over.id) {
            return;
        }

        const mealTypes = ['breakfast', 'lunch', 'dinner'];
        const oldIndex = mealTypes.indexOf(active.id);
        const newIndex = mealTypes.indexOf(over.id);

        if (oldIndex === -1 || newIndex === -1) {
            return;
        }

        // Actualizar el orden localmente (optimistic update)
        const reorderedMenu = { ...localMenu };
        const oldMealType = mealTypes[oldIndex];
        const newMealType = mealTypes[newIndex];

        // Intercambiar los meals
        const tempMeal = reorderedMenu[oldMealType];
        reorderedMenu[oldMealType] = reorderedMenu[newMealType];
        reorderedMenu[newMealType] = tempMeal;

        // Actualizar el mealType de cada objeto para que coincida con su nueva posición
        if (reorderedMenu[oldMealType]) {
            reorderedMenu[oldMealType] = { ...reorderedMenu[oldMealType], mealType: oldMealType };
        }
        if (reorderedMenu[newMealType]) {
            reorderedMenu[newMealType] = { ...reorderedMenu[newMealType], mealType: newMealType };
        }

        setLocalMenu(reorderedMenu);

        try {
            setIsReordering(true);

            // Crear el mapeo de tipos para el backend
            const typeMapping = {};
            typeMapping[oldMealType] = newMealType;
            typeMapping[newMealType] = oldMealType;

            // Llamar a la función de reordenamiento del padre
            await onMealReorder(typeMapping);

            // Si llegamos aquí, la operación fue exitosa
            // El estado local ya está actualizado, no necesitamos hacer nada más
        } catch (error) {
            // Solo revertir el cambio local en caso de error
            setLocalMenu(menu);
            setNotification({ isOpen: true, type: 'error', message: t('menu.errors.reorderFailed') });
        } finally {
            setIsReordering(false);
        }
    };

    // Función para manejar el toggle de repetible
    const handleToggleRepeatable = async (mealId) => {
        if (!onToggleRepeatable || !mealId) return;

        try {
            // Optimistic update
            setRepeatableStates(prev => ({
                ...prev,
                [mealId]: !prev[mealId]
            }));

            // Llamar al callback del padre
            await onToggleRepeatable(mealId);
        } catch (error) {
            // Revertir el cambio en caso de error
            setRepeatableStates(prev => ({
                ...prev,
                [mealId]: !prev[mealId]
            }));
            setNotification({ isOpen: true, type: 'error', message: t('menu.errors.updateRepeatFailed') });
        }
    };

    const handleViewDetails = (mealId) => {
        // Buscar el meal en localMenu para obtener su recipe_id
        let recipeId = mealId; // Fallback al mealId si no se encuentra
        if (localMenu) {
            for (const mealType of ['breakfast', 'lunch', 'dinner']) {
                if (localMenu[mealType] && localMenu[mealType].id === mealId) {
                    recipeId = localMenu[mealType].recipe_id || mealId;
                    break;
                }
            }
        }
        navigate(`/recipe/${recipeId}`);
    };

    // Función para abrir el selector de recetas
    const handleOpenRecipeSelector = (mealId, mealType) => {
        setSelectedMealToReplace({ id: mealId, type: mealType });
        setIsRecipeSelectorOpen(true);
    };

    // Función para manejar la selección de receta
    const handleRecipeSelected = async (recipeId) => {
        if (!selectedMealToReplace) return;

        setIsAssigning(true);

        try {
            const result = await mealService.assignRecipeToSlot(
                selectedMealToReplace.id,
                recipeId
            );

            if (result.success) {
                // Actualizar el meal en el menú local
                const updatedMenu = { ...localMenu };
                const mealKey = Object.keys(updatedMenu).find(
                    key => updatedMenu[key]?.id === selectedMealToReplace.id
                );

                if (mealKey) {
                    updatedMenu[mealKey] = result.meal;
                    setLocalMenu(updatedMenu);
                }

                setIsRecipeSelectorOpen(false);
                setSelectedMealToReplace(null);

                setNotification({ isOpen: true, type: 'success', message: t('menu.success.recipeAssigned') });
            }
        } catch (error) {
            setNotification({ isOpen: true, type: 'error', message: (t('menu.errors.assignFailed') + ': ' + error.message) });
        } finally {
            setIsAssigning(false);
        }
    };

    // Obtenemos las claves de las comidas (breakfast, lunch, dinner) directamente del objeto menú.
    const meals = localMenu ? Object.keys(localMenu) : [];

    if (meals.length === 0) {
        return <div className="p-4 text-gray-500">{t('menu.display.noDailyMenu') || t('common.notAvailable')}</div>;
    }

    // Componente para cada meal sortable
    const SortableMealCard = ({ mealKey, dish }) => {
        const {
            attributes,
            listeners,
            setNodeRef,
            transform,
            transition,
            isDragging,
        } = useSortable({ id: mealKey });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.5 : 1,
        };

        // Crear botón para seleccionar de biblioteca (solo si hay menuMetadata con status draft)



        return (
            <MealCard
                meal={dish}
                mealKey={mealKey}
                canGiveFeedback={canGiveFeedback}
                canReorder={canReorder}
                evaluatedMeals={evaluatedMeals}
                repeatableStates={repeatableStates}
                onFeedback={handleFeedback}
                onToggleRepeatable={handleToggleRepeatable}
                onViewDetails={handleViewDetails}
                ActionsComponent={MealFeedbackActions}
                dragProps={{
                    attributes,
                    listeners,
                    setNodeRef
                }}
                dragStyle={style}
            />
        );
    };

    return (
        <Card className="max-w-4xl mx-auto">
            <Card.Header>
                <Card.Title className="text-xl md:text-2xl font-bold text-primary-700">
                    {t('menu.display.generatedTitle')}
                </Card.Title>
                {canReorder && (
                    <div className="mt-2 text-xs sm:text-sm text-gray-600 text-center px-2">
                        💡 <span className="hidden sm:inline">{t('menu.display.dragMealsHint')}</span><span className="sm:hidden">{t('menu.display.holdToReorder')}</span>
                    </div>
                )}
            </Card.Header>

            {isReordering && (
            <div className="mb-4 p-2 bg-blue-50 text-blue-700 rounded-lg text-center text-sm">
                    <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    {t('common.updating')}
                    </div>
                </div>
            )}

            {canReorder ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={meals}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="space-y-6">
                            {meals.map((mealKey) => (
                                <SortableMealCard
                                    key={mealKey}
                                    mealKey={mealKey}
                                    dish={localMenu[mealKey]}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            ) : (
                <div className="space-y-6">
                    {meals.map((mealKey) => (
                        <SortableMealCard
                            key={mealKey}
                            mealKey={mealKey}
                            dish={localMenu[mealKey]}
                        />
                    ))}
                </div>
            )}

            {/* Modal para seleccionar receta de biblioteca */}
            <RecipeSelectorModal
                isOpen={isRecipeSelectorOpen}
                onClose={() => {
                    setIsRecipeSelectorOpen(false);
                    setSelectedMealToReplace(null);
                }}
                onSelectRecipe={handleRecipeSelected}
                mealType={selectedMealToReplace?.type}
            />

            {/* Modal de notificación */}
            <NotificationModal
                isOpen={notification.isOpen}
                onClose={() => setNotification({ ...notification, isOpen: false })}
                type={notification.type}
                message={notification.message}
            />
        </Card>
    );
};

export default MenuDisplay;
